/**
 * All DOM UI: the Bluesky login screen, the ready/menu screen (with a volume
 * slider + achievements button), the in-game top bar (layer / lives / tanks
 * left), the game-over screen, the achievements grid, plus transient banners and
 * unlock toasts. The game (main.ts) wires callbacks and pushes state via update().
 */

import { ACHIEVEMENTS, FLAIR_LABELS, type Achievement } from "../data/achievements";
import { UPGRADES, symbolSvg, type UpgradeDef } from "../systems/upgrades";
import { sfx } from "../engine/sfx";
import { injectStyles as injectCardStyles } from "./guidebook";
import type { Tank } from "../entities/tank";
import type { MusicTrack } from "../engine/music";

const SCORE_DIGITS = 7; // score odometer width
const CASH_DIGITS = 6; // cash odometer width (card mode)
const REEL_H = 34; // px height of one digit cell / reel step

export interface HudState {
  layer: number;
  lives: number;
  maxLives: number;
  shields: number;
  tanksLeft: number;
}

/** Persistent per-card DOM refs (mutated each frame so the live portrait <canvas> survives). */
interface CardRefs { canvas: HTMLCanvasElement; nameEl: HTMLDivElement; lvlEl: HTMLDivElement; costEl: HTMLDivElement; }

const FONT = "'Courier New', monospace";
// ---- the points-odometer visual language, shared across the whole UI ----
const GOLD = "#ffd86b";
const CYAN = "#7fd0ff";
const METAL_FRAME = "linear-gradient(180deg,#eef1f4,#bcc4ce 46%,#828b97)"; // brushed-metal rim
const METAL_CELL = "linear-gradient(180deg,#16181b,#34383d 16%,#454a51 50%,#34383d 84%,#121417)"; // dark digit cell
const METAL_EDGE = "#6a727e";
const PANEL_BG = "rgba(14,17,22,0.97)";
const FRAME_SHADOW = "0 4px 11px rgba(0,0,0,0.55),inset 0 1px 0 #ffffff,inset 0 -1px 0 #5b626d";
// menu (title-screen) backdrop: translucent vignette so the attract demo shows through.
const BACKDROP_DEMO = "radial-gradient(circle at 50% 42%, rgba(6,8,12,0.80) 0%, rgba(6,8,12,0.55) 32%, rgba(8,11,16,0.24) 100%)";
// in-game backdrop (pause/shop/game-over): near-opaque so small panel text stays legible over the frozen scene.
const BACKDROP_SOLID = "radial-gradient(circle at 50% 40%, rgba(18,23,32,0.93), rgba(7,9,13,0.97))";
/** the Bluesky labeler account players subscribe to (the zombies labeler) */
const LABELER_PROFILE = "https://bsky.app/profile/did:plc:7rfpwy2sld2akrgc42scyaau";

/** Lift a 0xRRGGBB colour toward readable luminance on a dark chip, keeping its hue. */
function readableHex(hex: number): string {
  let r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  if (lum < 150) { const k = (150 - lum) / (255 - lum); r += (255 - r) * k; g += (255 - g) * k; b += (255 - b) * k; }
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, css: string, html = ""): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.style.cssText = css;
  if (html) e.innerHTML = html;
  return e;
}

export class Hud {
  // callbacks (wired by Game)
  onLogin: (handle: string) => void = () => {};
  onPlay: () => void = () => {};
  onPlayCard: () => void = () => {};
  onCardPick: (i: number) => void = () => {};
  onResumeGame: () => void = () => {};
  onRestart: () => void = () => {};
  onMainMenu: () => void = () => {};
  onShowAchievements: () => void = () => {};
  onAchClose: () => void = () => {};
  onShowGuide: () => void = () => {};
  onLogout: () => void = () => {};
  onResetData: () => void = () => {};
  onSelectFlair: (value: string) => void = () => {};
  onVolume: (v: number) => void = () => {};
  onMusicVolume: (v: number) => void = () => {};
  getCurrentTrack: () => MusicTrack | undefined = () => undefined;
  onResume: () => void = () => {};
  onStartRound: () => void = () => {};

  private root: HTMLDivElement;
  private overlay: HTMLDivElement;
  private topbar: HTMLDivElement;
  private banner: HTMLDivElement;
  private toasts: HTMLDivElement;
  private killFeed!: HTMLDivElement;
  private statusLine: HTMLDivElement;
  private draftsEl: HTMLDivElement;
  private draftPips = new Map<string, { el: HTMLDivElement; count: number; badge: HTMLSpanElement }>();
  private bannerTimer: ReturnType<typeof setTimeout> | null = null;
  private volume = 0.5;
  private musicVolume = 0.5;
  private musicToasts!: HTMLDivElement; // bottom-right "now playing" credit stack
  // title-screen panel drag offset (so the player can shove the card aside to watch the demo)
  private panelOffX = 0;
  private panelOffY = 0;

  // rolling-odometer score counter (top-right)
  private scoreFrame!: HTMLDivElement;
  private scoreToast!: HTMLDivElement;
  private scoreCells: HTMLDivElement[] = []; // cell[place] (0 = ones)
  private scoreReels: HTMLDivElement[] = []; // reel[place]
  private scoreDisplayed = 0; // value currently shown (floats during the count-up)
  private scoreTarget = 0; // value rolling toward
  private scoreBurstStart = 0; // value when the current count-up began (for tick pitch)
  private scoreRAF = 0;
  private scoreLastTick = 0;
  // cash odometer (card mode only) — a 2nd rolling counter left of the score, labeled "$"
  private cashFrame!: HTMLDivElement;
  private cashCells: HTMLDivElement[] = [];
  private cashReels: HTMLDivElement[] = [];

  constructor() {
    this.root = el("div", "position:fixed;inset:0;z-index:10;pointer-events:none;color:#e8e2d4;font-family:" + FONT);
    document.body.appendChild(this.root);

    this.topbar = el("div",
      "position:absolute;top:14px;left:50%;transform:translateX(-50%);display:none;gap:26px;" +
      "align-items:center;padding:9px 22px;border-radius:7px;background:" + METAL_CELL + ";" +
      "border:1px solid " + METAL_EDGE + ";box-shadow:0 4px 11px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.06);" +
      "font-size:18px;letter-spacing:1px;white-space:nowrap");
    this.root.appendChild(this.topbar);

    this.banner = el("div",
      "position:absolute;top:38%;left:50%;transform:translate(-50%,-50%);display:none;" +
      "font-size:40px;font-weight:bold;text-align:center;text-shadow:0 3px 10px #000;color:#ffd86b");
    this.root.appendChild(this.banner);

    this.buildOdometer();

    this.toasts = el("div", "position:absolute;top:118px;right:18px;display:flex;flex-direction:column;gap:8px;align-items:flex-end");
    this.root.appendChild(this.toasts);

    // "now playing" music-credit stack — bottom-right, styled like the points odometer
    this.musicToasts = el("div", "position:absolute;bottom:18px;right:18px;display:flex;flex-direction:column;gap:8px;align-items:flex-end;pointer-events:none");
    this.root.appendChild(this.musicToasts);

    // kill feed (card mode): a stack of fading "KILLER ▸ VICTIM · Nm" lines, top-left under the floor bar
    this.killFeed = el("div", "position:absolute;top:62px;left:16px;display:flex;flex-direction:column;gap:3px;pointer-events:none;max-width:44vw");
    this.root.appendChild(this.killFeed);

    this.statusLine = el("div",
      "position:absolute;bottom:14px;left:50%;transform:translateX(-50%);font-size:14px;color:#9aa6b5;text-align:center");
    this.root.appendChild(this.statusLine);

    // collected-upgrade pips (bottom-left)
    this.draftsEl = el("div", "position:absolute;bottom:14px;left:16px;display:flex;gap:6px;flex-wrap:wrap;max-width:40vw");
    this.root.appendChild(this.draftsEl);

    // translucent vignette: a dark halo frames the centred panel for legibility,
    // fading to mostly-clear at the edges so the title-screen battle (and the frozen
    // scene behind the pause/shop/game-over panels) shows through.
    this.overlay = el("div",
      "position:absolute;inset:0;display:none;align-items:center;justify-content:center;pointer-events:auto;" +
      `background:${BACKDROP_DEMO}`);
    this.root.appendChild(this.overlay);

    this.fadeEl = el("div",
      "position:absolute;inset:0;background:#0a0d12;opacity:0;pointer-events:none;transition:opacity 0.32s ease");
    this.root.appendChild(this.fadeEl);
  }

  private fadeEl: HTMLDivElement;

  /** Fade to black, run `atBlack`, fade back in. */
  fadeThrough(atBlack: () => void): void {
    this.fadeEl.style.opacity = "1";
    setTimeout(() => {
      atBlack();
      this.fadeEl.style.opacity = "0";
    }, 340);
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? "block" : "none";
  }

  setStatus(msg: string): void {
    this.statusLine.textContent = msg;
  }

  // ---- panels ----------------------------------------------------------

  private panel(html: string): HTMLDivElement {
    // every dialog sits in a brushed-metal rim, like the odometer frame
    const frame = el("div", `padding:3px;border-radius:11px;background:${METAL_FRAME};box-shadow:0 22px 60px rgba(0,0,0,0.65)`);
    const card = el("div",
      "min-width:360px;max-width:560px;padding:32px 38px;border-radius:9px;background:" + PANEL_BG + ";" +
      "border:1px solid #0a0b0d;text-align:center", html);
    frame.appendChild(card);
    this.overlay.innerHTML = "";
    this.overlay.appendChild(frame);
    this.overlay.style.background = BACKDROP_SOLID; // default; the title screens override to the see-through vignette
    this.overlay.style.display = "flex";
    return card;
  }

  /** Make a centred panel `frame` draggable so the player can shove it aside to watch
   *  the attract demo. The stored offset persists across title-screen swaps. Grabbing
   *  an input/button/link does NOT start a drag, so the controls keep working. */
  private makeDraggable(frame: HTMLElement): void {
    frame.style.cursor = "grab";
    frame.style.touchAction = "none";
    const clamp = () => {
      const halfW = window.innerWidth / 2, halfH = window.innerHeight / 2, margin = 70;
      this.panelOffX = Math.max(margin - halfW, Math.min(halfW - margin, this.panelOffX));
      this.panelOffY = Math.max(margin - halfH, Math.min(halfH - margin, this.panelOffY));
    };
    const apply = () => { frame.style.transform = `translate(${this.panelOffX}px, ${this.panelOffY}px)`; };
    clamp(); apply(); // re-apply the persisted position when this panel is (re)shown
    let dragging = false, sx = 0, sy = 0, ox = 0, oy = 0;
    frame.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest("input,button,a,select,textarea,label")) return; // let controls work
      dragging = true; sx = e.clientX; sy = e.clientY; ox = this.panelOffX; oy = this.panelOffY;
      frame.style.cursor = "grabbing";
      try { frame.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      e.preventDefault();
    });
    frame.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      this.panelOffX = ox + (e.clientX - sx);
      this.panelOffY = oy + (e.clientY - sy);
      clamp(); apply();
    });
    const end = (e: PointerEvent) => { dragging = false; frame.style.cursor = "grab"; try { frame.releasePointerCapture(e.pointerId); } catch { /* ignore */ } };
    frame.addEventListener("pointerup", end);
    frame.addEventListener("pointercancel", end);
  }

  hideOverlay(): void {
    this.overlay.style.display = "none";
  }

  private button(label: string, primary = false): HTMLButtonElement {
    return el("button",
      `margin:8px 6px;padding:12px 22px;font-family:${FONT};font-size:16px;font-weight:bold;cursor:pointer;letter-spacing:1px;` +
      `border-radius:6px;border:1px solid ${primary ? GOLD : METAL_EDGE};` +
      `background:${primary ? "linear-gradient(180deg,#5a4a1e,#392e12)" : METAL_CELL};` +
      `color:${primary ? GOLD : "#f2efe6"};box-shadow:0 3px 8px rgba(0,0,0,0.45),inset 0 1px 0 rgba(255,255,255,0.06)`,
      label);
  }

  showLogin(msg = ""): void {
    const card = this.panel(
      `<div style="font-size:46px;font-weight:bold;letter-spacing:6px;color:#ffd86b">TANKS</div>
       <div style="margin:10px 0 22px;color:#9aa6b5;font-size:15px;line-height:1.5">
         An endless tower of tank battles.<br>Sign in with Bluesky and your <b>mutuals</b> become the enemy tanks.</div>`);
    this.overlay.style.background = BACKDROP_DEMO; // title screen — let the attract demo show through
    if (card.parentElement) this.makeDraggable(card.parentElement); // drag the card aside to watch the demo
    const input = el("input",
      `width:100%;padding:12px 14px;margin-bottom:6px;font-family:${FONT};font-size:16px;` +
      "border:1px solid #3a4250;background:#0e1218;color:#fff;text-align:center");
    (input as HTMLInputElement).placeholder = "you.bsky.social";
    card.appendChild(input);
    const err = el("div", "color:#ff8a7a;font-size:13px;min-height:18px;margin-bottom:6px", msg);
    card.appendChild(err);
    const go = this.button("SIGN IN WITH BLUESKY", true);
    go.onclick = () => this.onLogin((input as HTMLInputElement).value.trim());
    input.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") go.click(); });
    card.appendChild(go);
    card.appendChild(el("div", "margin-top:16px;color:#5f6b7a;font-size:12px",
      "Read-only sign-in · we never post · stats live in your own PDS"));
    this.overlay.style.display = "flex";
    setTimeout(() => (input as HTMLInputElement).focus(), 30);
  }

  showReady(handle: string, achCount: number, achTotal: number, highestLayer: number, savedLayer: number, cardSavedLayer = 0, cardUnlocked = true): void {
    const card = this.panel(
      `<div style="font-size:40px;font-weight:bold;letter-spacing:5px;color:#ffd86b">TANKS</div>
       <div style="margin:8px 0 4px;color:#9aa6b5">signed in as <b style="color:#cfe0ff">@${handle}</b></div>
       <div style="margin:2px 0 18px;color:#8a96a5;font-size:14px">
         best climb: <b style="color:#fff">layer ${highestLayer}</b> · achievements ${achCount}/${achTotal}</div>`);
    this.overlay.style.background = BACKDROP_DEMO; // title screen — let the attract demo show through
    if (card.parentElement) this.makeDraggable(card.parentElement); // drag the card aside to watch the demo

    if (savedLayer > 0) {
      const resume = this.button(`RESUME — LAYER ${savedLayer}`, true);
      resume.onclick = () => this.onResumeGame();
      card.appendChild(resume);
      const fresh = this.button("NEW CLIMB");
      fresh.onclick = () => this.onPlay();
      card.appendChild(fresh);
    } else {
      const play = this.button("START CLIMB", true);
      play.onclick = () => this.onPlay();
      card.appendChild(play);
    }

    // CARD MODE — a separate deckbuilder run on its own save slot; UNLOCKED by reaching
    // layer 25 in a normal climb (the "Skill: Average" achievement)
    if (cardUnlocked) {
      const cm = this.button(cardSavedLayer > 0 ? `CARD MODE — FLOOR ${cardSavedLayer}` : "CARD MODE");
      cm.onclick = () => this.onPlayCard();
      card.appendChild(cm);
    } else {
      const cm = this.button("CARD MODE");
      cm.style.opacity = "0.45";
      cm.style.cursor = "not-allowed";
      cm.onclick = () => { cm.animate([{ transform: "translateX(-3px)" }, { transform: "translateX(3px)" }, { transform: "translateX(0)" }], { duration: 180 }); };
      card.appendChild(cm);
      card.appendChild(el("div", "margin:-2px 0 2px;color:#ffb454;font-size:12px;letter-spacing:0.5px",
        "LOCKED — reach layer 25 in a climb to unlock"));
    }

    card.appendChild(el("div", "height:1px;background:#2a323e;margin:18px 0"));
    const ach = this.button("ACHIEVEMENTS");
    ach.onclick = () => this.onShowAchievements();
    card.appendChild(ach);

    // volume slider
    const volRow = el("div", "margin:18px 0 4px;display:flex;align-items:center;gap:10px;justify-content:center;color:#9aa6b5;font-size:14px");
    volRow.appendChild(el("span", "", "VOL"));
    const slider = el("input", "width:160px");
    (slider as HTMLInputElement).type = "range";
    (slider as HTMLInputElement).min = "0";
    (slider as HTMLInputElement).max = "100";
    (slider as HTMLInputElement).value = String(Math.round(this.volume * 100));
    slider.oninput = () => { this.volume = Number((slider as HTMLInputElement).value) / 100; this.onVolume(this.volume); };
    volRow.appendChild(slider);
    card.appendChild(volRow);

    const small = el("div", "margin-top:14px;display:flex;gap:14px;justify-content:center");
    const logout = el("button", `background:none;border:none;color:#6f7b8a;cursor:pointer;font-family:${FONT};font-size:13px;text-decoration:underline`, "log out");
    logout.onclick = () => this.onLogout();
    const reset = el("button", `background:none;border:none;color:#6f7b8a;cursor:pointer;font-family:${FONT};font-size:13px;text-decoration:underline`, "reset data");
    reset.onclick = () => { if (confirm("Erase all saved progress (achievements, best climb)?")) this.onResetData(); };
    small.appendChild(logout);
    small.appendChild(reset);
    card.appendChild(small);

    card.appendChild(el("div", "margin-top:18px;color:#5f6b7a;font-size:12px;line-height:1.6",
      "WASD / arrows drive · mouse aims · click fires · Space lays a mine<br>" +
      "scroll = zoom · Q / E = rotate camera<br>" +
      "shells ricochet — clear every tank, then ride the elevator up"));
    this.overlay.style.display = "flex";
  }

  showGameOver(layer: number, kills: number, score: number, line: string): void {
    const card = this.panel(
      `<div style="font-size:38px;font-weight:bold;color:#ff7a6b;letter-spacing:3px">DESTROYED</div>
       <div style="margin:12px 0 1px;font-size:13px;color:#9aa6b5;letter-spacing:3px">SCORE</div>
       <div style="font-size:40px;font-weight:bold;color:#ffd86b;font-variant-numeric:tabular-nums">${score.toLocaleString()}</div>
       <div style="margin:10px 0 4px;font-size:18px">reached <b style="color:#ffd86b">layer ${layer}</b></div>
       <div style="margin:2px 0 6px;color:#9aa6b5">${kills} tanks destroyed this run</div>
       <div style="margin:6px 0 18px;color:#8a96a5;font-size:14px">${line}</div>`);
    const again = this.button("CLIMB AGAIN", true);
    again.onclick = () => this.onRestart();
    card.appendChild(again);
    const menu = this.button("MAIN MENU");
    menu.onclick = () => this.onMainMenu();
    card.appendChild(menu);
    this.overlay.style.display = "flex";
  }

  showAchievements(unlocked: Set<string>, worn: string, justWore?: string): void {
    const card = this.panel(
      `<div style="font-size:28px;font-weight:bold;color:#ffd86b;letter-spacing:2px">ACHIEVEMENTS</div>
       <div style="margin:6px 0 14px;color:#9aa6b5;font-size:13px">${unlocked.size}/${ACHIEVEMENTS.length} unlocked · ` +
       `<a href="${LABELER_PROFILE}" target="_blank" rel="noopener noreferrer" style="color:#7fd0ff;pointer-events:auto" ` +
       `title="subscribe to see worn achievements on Bluesky profiles">⊕ labeler ↗</a></div>`);

    if (justWore) {
      const wa = ACHIEVEMENTS.find((a) => FLAIR_LABELS.find((f) => f.ach === a.id)?.value === justWore);
      const info = el("div",
        "max-width:520px;margin-bottom:12px;text-align:center;padding:10px 14px;pointer-events:auto;" +
        "background:rgba(70,130,180,0.16);border:1px solid #5b8fb0",
        `<div style="font-weight:bold;font-size:14px;color:#cfe8ff">Now wearing “${this.escape(wa?.name ?? justWore)}”.</div>` +
        `<div style="font-size:12px;color:#bcd;margin-top:3px">You must be <b>subscribed to the labeler</b> for it to show on Bluesky profiles ` +
        `(each person subscribes for themselves; give it a moment).</div>` +
        `<a href="${LABELER_PROFILE}" target="_blank" rel="noopener noreferrer" ` +
        `style="display:inline-block;margin-top:8px;font-weight:bold;font-size:13px;color:#0b1a26;background:#9cd2f0;padding:5px 14px;pointer-events:auto;text-decoration:none">⊕ Subscribe to the labeler ↗</a>`);
      card.appendChild(info);
    }

    const grid = el("div", "display:flex;flex-direction:column;gap:6px;max-height:46vh;overflow:auto;text-align:left;padding-right:6px;pointer-events:auto");
    for (const a of ACHIEVEMENTS) {
      const got = unlocked.has(a.id);
      const slug = FLAIR_LABELS.find((f) => f.ach === a.id)?.value ?? "";
      const isWorn = got && worn === slug;
      const row = el("div",
        `padding:8px 12px;border:1px solid ${isWorn ? "#7ee65a" : "#2a323e"};` +
        `background:${got ? "rgba(255,210,60,0.12)" : "rgba(20,24,32,0.5)"};${got ? "" : "opacity:0.5"};` +
        "display:flex;justify-content:space-between;align-items:center;gap:10px");
      row.innerHTML =
        `<div style="overflow:hidden"><div style="font-weight:bold;color:${got ? "#ffe27a" : "#8a96a5"}">${this.escape(a.name)}</div>` +
        `<div style="font-size:12px;color:${got ? "#cdb" : "#5a6478"}">${this.escape(a.desc)}</div></div>`;
      if (got) {
        const btn = el("button",
          `flex:none;pointer-events:auto;cursor:pointer;font-family:${FONT};font-weight:bold;font-size:12px;padding:5px 12px;` +
          `border:1px solid ${isWorn ? "#7ee65a" : "#456"};background:${isWorn ? "rgba(126,230,90,0.22)" : "rgba(20,27,38,0.6)"};color:#fff`,
          isWorn ? "★ worn" : "wear");
        btn.onclick = () => this.onSelectFlair(isWorn ? "" : slug);
        row.appendChild(btn);
      }
      grid.appendChild(row);
    }
    card.appendChild(grid);
    const close = this.button("BACK", true);
    close.onclick = () => this.onAchClose();
    card.appendChild(close);
    this.overlay.style.display = "flex";
  }

  // ---- pause ----------------------------------------------------------

  showPaused(): void {
    const card = this.panel(
      `<div style="font-size:48px;font-weight:bold;letter-spacing:4px;color:#fff;text-shadow:0 3px 10px #000">PAUSED</div>
       <div style="margin:4px 0 16px;color:#9aa6b5;letter-spacing:2px;font-size:14px">— Esc to resume —</div>`);
    const mkSlider = (label: string, value: number, onChange: (v: number) => void) => {
      const row = el("div", "margin:6px 0;display:flex;align-items:center;gap:10px;justify-content:space-between;color:#9aa6b5;font-size:13px");
      row.appendChild(el("span", "min-width:54px;text-align:left", label));
      const s = el("input", "width:170px");
      (s as HTMLInputElement).type = "range"; (s as HTMLInputElement).min = "0"; (s as HTMLInputElement).max = "100";
      (s as HTMLInputElement).value = String(Math.round(value * 100));
      s.oninput = () => onChange(Number((s as HTMLInputElement).value) / 100);
      row.appendChild(s);
      card.appendChild(row);
    };
    mkSlider("GAME", this.volume, (v) => { this.volume = v; this.onVolume(v); });
    mkSlider("MUSIC", this.musicVolume, (v) => { this.musicVolume = v; this.onMusicVolume(v); });
    const cur = this.getCurrentTrack();
    if (cur) {
      const lk = el("a", "display:block;margin:8px 0 14px;color:" + CYAN + ";font-size:12px;text-decoration:none;pointer-events:auto;white-space:nowrap;overflow:hidden;text-overflow:ellipsis",
        `♪ ${this.escape(cur.title)} <span style="color:#7a8696">— watch ▸</span>`);
      (lk as HTMLAnchorElement).href = cur.url;
      (lk as HTMLAnchorElement).target = "_blank";
      (lk as HTMLAnchorElement).rel = "noopener noreferrer";
      lk.title = cur.url;
      card.appendChild(lk);
    } else {
      card.appendChild(el("div", "height:8px"));
    }
    const guide = this.button("FIELD GUIDE");
    guide.onclick = () => this.onShowGuide();
    card.appendChild(guide);
    const ach = this.button("ACHIEVEMENTS");
    ach.onclick = () => this.onShowAchievements();
    card.appendChild(ach);
    const resume = this.button("RESUME", true);
    resume.onclick = () => this.onResume();
    card.appendChild(resume);
    card.appendChild(el("div", "height:1px;background:#2a323e;margin:14px 0"));
    const menu = this.button("QUIT TO MENU");
    menu.onclick = () => this.onMainMenu();
    card.appendChild(menu);
    this.overlay.style.display = "flex";
  }

  // ---- card mode: the hand + cash bar --------------------------------

  private cardBar?: HTMLDivElement;
  private cardEls: HTMLButtonElement[] = [];
  private startBtn?: HTMLButtonElement;
  private cardCanvases: HTMLCanvasElement[] = []; // live 3D portrait targets (one per hand slot)

  /** Build (once) + show the bottom hand bar for card mode. Cards reuse the field guide's
   *  liquid-glass look + live 3D tank portraits (main.ts drives the portrait render loop). */
  showCardBar(slots: number): void {
    if (!this.cardBar) {
      injectCardStyles(); // the .gb-card liquid-glass CSS (shared with the field guide)
      this.cardBar = el("div",
        "position:absolute;bottom:16px;left:50%;transform:translateX(-50%);display:flex;align-items:flex-end;gap:12px;pointer-events:none");
      this.root.appendChild(this.cardBar);
      for (let i = 0; i < slots; i++) {
        const b = document.createElement("button");
        b.className = "gb-card"; // glass panel + chroma rim + drifting lattice (width overridden below)
        b.style.cssText = `pointer-events:auto;cursor:pointer;width:128px;padding:0;font-family:${FONT};color:#fff;text-align:center;display:flex;flex-direction:column`;
        const idx = i;
        // consume the click so it never bleeds into the game's fire/placement input
        b.addEventListener("mousedown", (e) => { e.stopPropagation(); e.preventDefault(); });
        b.onclick = (e) => { e.stopPropagation(); this.onCardPick(idx); };
        const canvas = document.createElement("canvas");
        canvas.width = 252; canvas.height = 200; // matches the guidebook offscreen size
        b.appendChild(canvas);
        const info = el("div", "padding:5px 8px 4px;display:flex;flex-direction:column;gap:2px;align-items:center");
        const nameEl = el("div", "font-weight:900;font-size:14px;letter-spacing:0.5px;line-height:1.05;text-shadow:0 1px 3px #000");
        const lvlEl = el("div", "font-size:11px;color:#aab4c2");
        const costEl = el("div", "font-size:13px;font-weight:bold");
        info.append(nameEl, lvlEl, costEl);
        b.appendChild(info);
        (b as unknown as { _refs: CardRefs })._refs = { canvas, nameEl, lvlEl, costEl };
        this.cardEls.push(b);
        this.cardCanvases[i] = canvas;
        this.cardBar.appendChild(b);
      }
    }
    this.cardBar.style.display = "flex";
  }

  /** The live-portrait canvas for hand slot `i` (main.ts renders into it each frame). */
  cardCanvas(i: number): HTMLCanvasElement | undefined { return this.cardCanvases[i]; }

  hideCardBar(): void { if (this.cardBar) this.cardBar.style.display = "none"; }

  /** Card-mode top readout: floor + enemies remaining + your tank count (no hearts).
   *  Anchored TOP-LEFT (the cash odometer takes the top-right, beside the score). */
  updateCardTop(layer: number, enemiesLeft: number, tanks: number): void {
    this.scoreFrame.style.display = "flex";
    this.scoreToast.style.display = "flex";
    this.topbar.style.display = "flex";
    this.topbar.style.left = "18px";
    this.topbar.style.transform = "none";
    this.topbar.innerHTML =
      `<span>FLOOR <b style="color:${GOLD}">${layer}</b></span>` +
      `<span>ENEMIES <b style="color:#ff7a6b">${enemiesLeft}</b></span>` +
      `<span>TANKS <b style="color:${CYAN}">${tanks}</b></span>`;
  }

  /** Per-frame refresh of the hand: the cash odometer + each card's portrait accent /
   *  type / level / cost / selected-warn-afford state. Mutates persistent elements (never
   *  rewrites innerHTML) so the live 3D portrait <canvas> survives across frames. */
  updateCardBar(cash: number, cards: Array<{ name: string; color: number; level: number; cost: number; afford: boolean; badge: "off" | "neutral" | "up"; warn: boolean }>, selected: number): void {
    this.showCash(cash);
    const badgeCol = { off: "#45d09e", neutral: "#9aa6b5", up: "#ff7a8a" };
    for (let i = 0; i < this.cardEls.length; i++) {
      const b = this.cardEls[i], c = cards[i];
      if (!c) { b.style.display = "none"; continue; }
      b.style.display = "flex";
      const accent = readableHex(c.color);
      const sel = i === selected;
      const refs = (b as unknown as { _refs: CardRefs })._refs;
      b.style.setProperty("--tc", accent); // chroma rim + lattice tint key
      refs.nameEl.textContent = c.name; refs.nameEl.style.color = accent;
      refs.lvlEl.textContent = `Lv ${c.level}`;
      refs.costEl.textContent = `$${c.cost}`;
      refs.costEl.style.color = c.afford ? badgeCol[c.badge] : "#ff7a8a";
      // selection = gold ring; about-to-evolve = amber ring (outline keeps the glass rim intact)
      b.style.outline = sel ? `2px solid ${GOLD}` : c.warn ? "2px solid #ffae28" : "none";
      b.style.outlineOffset = "2px";
      b.style.opacity = c.afford ? "1" : "0.5";
    }
  }

  // ---- field guide (tank bestiary) -----------------------------------

  /** The pause-menu FIELD GUIDE: a wide scrollable panel wrapping the prebuilt card
   *  grid (3D tank renders + stats), with a BACK button that returns to the pause menu. */
  showGuide(content: HTMLElement, onBack: () => void): void {
    const frame = el("div", `padding:3px;border-radius:11px;background:${METAL_FRAME};box-shadow:0 22px 60px rgba(0,0,0,0.65)`);
    const card = el("div",
      `border-radius:9px;background:${PANEL_BG};border:1px solid #0a0b0d;padding:20px 24px 22px;text-align:center;` +
      "min-width:560px;max-width:860px;width:84vw;max-height:92vh;overflow:hidden;display:flex;flex-direction:column");
    card.appendChild(el("div", `font-size:26px;font-weight:bold;color:${GOLD};text-shadow:0 3px 10px #000;letter-spacing:2px`, "FIELD GUIDE"));
    card.appendChild(el("div", "margin:5px 0 14px;color:#9aa6b5;font-size:13px;letter-spacing:1px", "every tank you'll meet on the climb"));
    card.appendChild(content);
    const back = this.button("BACK", true);
    back.style.flex = "none";
    back.onclick = () => onBack();
    card.appendChild(back);
    frame.appendChild(card);
    this.overlay.innerHTML = "";
    this.overlay.appendChild(frame);
    this.overlay.style.background = BACKDROP_SOLID;
    this.overlay.style.display = "flex";
  }

  // ---- between-floor upgrade draft ------------------------------------

  showUpgradeDraft(layer: number, choices: UpgradeDef[], tally: Record<string, number>, onPick: (id: string) => void): void {
    const card = this.panel(
      `<div style="font-size:30px;font-weight:bold;color:#ffd86b;text-shadow:0 3px 10px #000">FLOOR ${layer} CLEARED</div>
       <div style="margin:4px 0 14px;color:#cdd;letter-spacing:1px">choose a free upgrade</div>`);
    const row = el("div", "display:flex;gap:16px;justify-content:center;flex-wrap:wrap");
    let chosen = false;
    for (const def of choices) {
      const hex = `#${def.color.toString(16).padStart(6, "0")}`;
      const have = tally[def.id] ?? 0;
      const cardBtn = el("button",
        `width:150px;padding:0 0 14px;cursor:pointer;font-family:${FONT};color:#fff;text-align:center;` +
        `background:#161b24;border:1px solid ${hex};display:flex;flex-direction:column;align-items:center;overflow:hidden`);
      cardBtn.innerHTML =
        `<div style="width:100%;height:8px;background:${hex}"></div>` +
        `<div style="width:54px;height:54px;margin:16px 0 8px">${symbolSvg(def.symbol, hex)}</div>` +
        `<div style="font-weight:bold;font-size:17px">${this.escape(def.name)}</div>` +
        `<div style="font-size:12px;color:#9aa6b5;margin:4px 10px 0">${this.escape(def.desc)}</div>` +
        (have > 0 ? `<div style="font-size:11px;color:${hex};margin-top:8px">owned ×${have}</div>` : "");
      cardBtn.onclick = () => {
        if (chosen) return;
        chosen = true;
        this.bumpDraftPip(def);
        onPick(def.id);
        this.overlay.style.display = "none";
      };
      row.appendChild(cardBtn);
    }
    card.appendChild(row);
    this.overlay.style.display = "flex";
  }

  // ---- round-end shop (odometer-themed) -------------------------------

  /** A static odometer-style readout (metallic rim + dark digit cells) for points/prices. */
  private odoChip(value: number, digits: number, cellW = 16, cellH = 24, fs = 19): string {
    const n = Math.max(0, Math.floor(value));
    digits = Math.max(digits, n.toString().length); // grow rather than truncate on huge scores
    const s = n.toString().padStart(digits, "0");
    const sig = n.toString().length;
    let cells = "";
    for (let i = 0; i < digits; i++) {
      const dim = digits - i > sig ? "0.3" : "1";
      cells += `<span style="display:inline-block;width:${cellW}px;height:${cellH}px;line-height:${cellH}px;margin:0 1px;` +
        `text-align:center;font-family:${FONT};font-weight:bold;font-size:${fs}px;color:#f2efe6;text-shadow:0 1px 2px #000;` +
        `border-radius:2px;background:${METAL_CELL};box-shadow:inset 0 0 5px rgba(0,0,0,0.85);` +
        `border-left:1px solid #0a0b0d;border-right:1px solid #0a0b0d;opacity:${dim}">${s[i]}</span>`;
    }
    return `<span style="display:inline-flex;align-items:center;padding:4px 6px;border-radius:6px;` +
      `background:${METAL_FRAME};box-shadow:${FRAME_SHADOW};border:1px solid ${METAL_EDGE}">${cells}</span>`;
  }

  /** One shop purchase button styled like an odometer cell, with an accent + price chip. */
  private shopButton(o: { title: string; sub: string; cost: number; enabled: boolean; accent: string; note: string; onClick: () => void }): HTMLButtonElement {
    const b = el("button",
      `width:210px;padding:14px 12px;font-family:${FONT};cursor:${o.enabled ? "pointer" : "default"};` +
      `border-radius:7px;border:1px solid ${METAL_EDGE};background:${METAL_CELL};color:#f2efe6;` +
      `box-shadow:0 4px 11px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.06);` +
      `display:flex;flex-direction:column;align-items:center;gap:7px;opacity:${o.enabled ? "1" : "0.5"}`);
    b.innerHTML =
      `<span style="display:inline-block;width:18px;height:18px;border-radius:4px;background:${o.accent};box-shadow:0 0 9px ${o.accent}"></span>` +
      `<span style="font-weight:bold;font-size:15px;color:${GOLD};letter-spacing:1px">${this.escape(o.title)}</span>` +
      `<span style="font-size:11px;color:#9aa6b5;min-height:26px">${this.escape(o.sub)}</span>` +
      this.odoChip(o.cost, 5, 13, 19, 15) +
      (o.enabled ? "" : `<span style="font-size:10px;color:#e07a5a;letter-spacing:1px">${this.escape(o.note)}</span>`);
    if (o.enabled) b.onclick = o.onClick;
    return b;
  }

  /** The round-end shop: pick a free perk, hire a mercenary, or reroll the perks. */
  showShop(o: {
    layer: number; choices: UpgradeDef[]; tally: Record<string, number>; points: number;
    mercCost: number; mercSoldOut: boolean; mercAffordable: boolean; allyCount: number; allyCap: number;
    respawnCost: number; respawnOwned: boolean; respawnAffordable: boolean;
    rerollCost: number; rerollAffordable: boolean;
    perkCost: (id: string) => number;
    onPick: (id: string) => void; onBuyMerc: () => void; onBuyRespawn: () => void; onReroll: () => void; onSkip: () => void;
  }): void {
    const frame = el("div", `padding:3px;border-radius:11px;background:${METAL_FRAME};box-shadow:0 22px 60px rgba(0,0,0,0.65)`);
    const card = el("div",
      `border-radius:9px;background:${PANEL_BG};border:1px solid #0a0b0d;padding:22px 30px 26px;text-align:center;` +
      "min-width:540px;max-width:760px;max-height:92vh;overflow-y:auto;overflow-x:hidden");

    card.appendChild(el("div", `font-size:27px;font-weight:bold;color:${GOLD};text-shadow:0 3px 10px #000;letter-spacing:1px`, `FLOOR ${o.layer} CLEARED`));
    const pts = el("div", "margin:9px 0 4px;display:flex;align-items:center;justify-content:center;gap:10px");
    pts.innerHTML = `<span style="color:#9aa6b5;letter-spacing:3px;font-size:12px">POINTS</span>` + this.odoChip(o.points, 7, 18, 28, 21);
    card.appendChild(pts);

    // ---- buy a perk (prices scale with depth, and steeply each time you re-take the SAME perk) ----
    card.appendChild(el("div", "margin:16px 0 9px;color:#cdd;letter-spacing:3px;font-size:11px", "— BUY A PERK —"));
    const row = el("div", "display:flex;gap:14px;justify-content:center;flex-wrap:wrap");
    for (const def of o.choices) {
      const hex = `#${def.color.toString(16).padStart(6, "0")}`;
      const have = o.tally[def.id] ?? 0;
      const cost = o.perkCost(def.id);
      const afford = o.points >= cost;
      const cardBtn = el("button",
        `width:148px;padding:0 0 13px;cursor:${afford ? "pointer" : "not-allowed"};font-family:${FONT};color:#fff;text-align:center;` +
        `background:#12161d;border:1px solid ${afford ? hex : "#3a3f47"};border-radius:6px;display:flex;flex-direction:column;align-items:center;overflow:hidden;opacity:${afford ? 1 : 0.5}`);
      cardBtn.innerHTML =
        `<div style="width:100%;height:8px;background:${hex}"></div>` +
        `<div style="width:52px;height:52px;margin:15px 0 8px">${symbolSvg(def.symbol, hex)}</div>` +
        `<div style="font-weight:bold;font-size:16px">${this.escape(def.name)}</div>` +
        `<div style="font-size:12px;color:#9aa6b5;margin:4px 10px 0">${this.escape(def.desc)}</div>` +
        (have > 0 ? `<div style="font-size:11px;color:${hex};margin-top:6px">owned ×${have}</div>` : "") +
        `<div style="font-size:14px;font-weight:bold;color:${afford ? GOLD : "#e07a7a"};margin-top:8px">${cost.toLocaleString()} pts</div>`;
      cardBtn.onclick = () => o.onPick(def.id);
      row.appendChild(cardBtn);
    }
    card.appendChild(row);

    // ---- spend points ----
    card.appendChild(el("div", "margin:20px 0 9px;color:#cdd;letter-spacing:3px;font-size:11px", "— SPEND POINTS —"));
    const shopRow = el("div", "display:flex;gap:16px;justify-content:center;flex-wrap:wrap");
    shopRow.appendChild(this.shopButton({
      title: "HIRE MERCENARY",
      sub: o.mercSoldOut ? `roster full (${o.allyCount}/${o.allyCap})` : `a tank that fights for you (${o.allyCount}/${o.allyCap})`,
      cost: o.mercCost, accent: "#2fd0c0",
      enabled: o.mercAffordable && !o.mercSoldOut,
      note: o.mercSoldOut ? "ROSTER FULL" : "NEED POINTS",
      onClick: () => o.onBuyMerc(),
    }));
    shopRow.appendChild(this.shopButton({
      title: "REINFORCEMENTS",
      sub: o.respawnOwned ? "owned — dead mercs redeploy mid-floor" : "dead mercs redeploy mid-floor, not just next floor",
      cost: o.respawnCost, accent: "#46e6b4",
      enabled: o.respawnAffordable && !o.respawnOwned,
      note: o.respawnOwned ? "OWNED" : "NEED POINTS",
      onClick: () => o.onBuyRespawn(),
    }));
    shopRow.appendChild(this.shopButton({
      title: "REROLL PERKS",
      sub: "swap in 3 fresh perks",
      cost: o.rerollCost, accent: GOLD,
      enabled: o.rerollAffordable,
      note: "NEED POINTS",
      onClick: () => o.onReroll(),
    }));
    card.appendChild(shopRow);
    card.appendChild(el("div", "margin-top:16px;color:#6f7a88;font-size:11px;letter-spacing:1px", "perk prices rise with depth — and more each time you re-take the same one"));
    const start = el("button",
      `margin-top:12px;padding:11px 34px;cursor:pointer;font-family:${FONT};font-weight:bold;font-size:15px;letter-spacing:2px;` +
      `color:#12161d;background:${GOLD};border:none;border-radius:7px`, "START");
    start.onclick = () => { this.overlay.style.display = "none"; o.onSkip(); };
    card.appendChild(start);

    frame.appendChild(card);
    this.overlay.innerHTML = "";
    this.overlay.appendChild(frame);
    this.overlay.style.background = BACKDROP_SOLID; // shop has small text over the frozen floor — keep it legible
    this.overlay.style.display = "flex";
  }

  hideShop(): void { this.overlay.style.display = "none"; this.overlay.innerHTML = ""; }

  clearDrafts(): void {
    this.draftsEl.innerHTML = "";
    this.draftPips.clear();
  }

  setDraftPips(counts: Record<string, number>): void {
    this.clearDrafts();
    for (const [id, n] of Object.entries(counts ?? {})) {
      const def = UPGRADES[id];
      if (!def || n <= 0) continue;
      for (let i = 0; i < n; i++) this.bumpDraftPip(def);
    }
  }

  private bumpDraftPip(def: UpgradeDef): void {
    let entry = this.draftPips.get(def.id);
    if (!entry) {
      const hex = `#${def.color.toString(16).padStart(6, "0")}`;
      const pip = el("div",
        `width:26px;height:26px;padding:4px;background:${hex};position:relative`, symbolSvg(def.symbol, "#fff"));
      pip.title = `${def.name}: ${def.desc}`;
      const badge = el("span",
        "position:absolute;right:-4px;bottom:-6px;font-size:10px;font-weight:bold;color:#fff;background:#000;padding:0 3px;display:none");
      pip.appendChild(badge);
      this.draftsEl.appendChild(pip);
      entry = { el: pip, count: 0, badge };
      this.draftPips.set(def.id, entry);
    }
    entry.count++;
    entry.badge.textContent = `×${entry.count}`;
    entry.badge.style.display = entry.count > 1 ? "block" : "none";
  }

  // ---- in-game --------------------------------------------------------

  private heart(filled: boolean): string {
    return `<span style="color:${filled ? "#ff5a4a" : "#3a4250"};font-size:20px">${filled ? "▰" : "▱"}</span>`;
  }

  update(s: HudState): void {
    this.cashFrame.style.display = "none"; // cash odometer is card-mode only
    if (this.overlay.style.display !== "none") {
      this.topbar.style.display = "none";
      this.draftsEl.style.display = "none";
      this.scoreFrame.style.display = "none";
      this.scoreToast.style.display = "none";
      return;
    }
    this.topbar.style.display = "flex";
    this.topbar.style.left = "50%"; // normal mode keeps the bar top-CENTER
    this.topbar.style.transform = "translateX(-50%)";
    this.draftsEl.style.display = "flex";
    this.scoreFrame.style.display = "flex";
    this.scoreToast.style.display = "flex";
    const lives = Array.from({ length: Math.max(s.lives, 0) }, () => this.heart(true)).join("") +
      Array.from({ length: Math.max(s.maxLives - s.lives, 0) }, () => this.heart(false)).join("");
    const shields = s.shields > 0
      ? `<span style="display:flex;gap:2px;align-items:center;color:${CYAN}">${"🛡".repeat(s.shields)}</span>` : "";
    this.topbar.innerHTML =
      `<span>LAYER <b style="color:${GOLD}">${s.layer}</b></span>` +
      `<span style="display:flex;gap:3px;align-items:center">${lives}</span>` +
      shields +
      `<span>TANKS LEFT <b style="color:${CYAN}">${s.tanksLeft}</b></span>`;
  }

  // ---- score odometer --------------------------------------------------

  /** Build one metallic rolling-digit strip (the shared odometer visual). `rightPx`
   *  anchors it from the right; an optional `label` (e.g. "$") prefixes the digits. */
  private buildOdoStrip(rightPx: number, digits: number, label?: string): { frame: HTMLDivElement; cells: HTMLDivElement[]; reels: HTMLDivElement[] } {
    const frame = el("div",
      `position:absolute;top:14px;right:${rightPx}px;display:none;align-items:center;padding:5px 7px;` +
      `border-radius:7px;background:${METAL_FRAME};box-shadow:${FRAME_SHADOW};border:1px solid ${METAL_EDGE}`);
    if (label) frame.appendChild(el("span",
      `font-family:${FONT};font-weight:bold;font-size:23px;color:${GOLD};margin:0 5px 0 2px;text-shadow:0 1px 2px #000`, label));
    const cells: HTMLDivElement[] = [], reels: HTMLDivElement[] = [];
    // most-significant digit first (left); store reels indexed by place (0 = ones)
    for (let i = 0; i < digits; i++) {
      const place = digits - 1 - i;
      const cell = el("div",
        `position:relative;width:22px;height:${REEL_H}px;overflow:hidden;margin:0 1px;border-radius:2px;` +
        "background:linear-gradient(180deg,#16181b,#34383d 16%,#454a51 50%,#34383d 84%,#121417);" +
        "box-shadow:inset 0 0 5px rgba(0,0,0,0.85),inset 0 1px 0 rgba(255,255,255,0.05);" +
        "border-left:1px solid #0a0b0d;border-right:1px solid #0a0b0d");
      const reel = el("div", "position:absolute;left:0;top:0;width:100%");
      for (let d = 0; d <= 10; d++) { // 0..9 plus a duplicate 0 so 9->0 rolls forward
        reel.appendChild(el("div",
          `height:${REEL_H}px;line-height:${REEL_H}px;text-align:center;font-family:${FONT};` +
          "font-size:25px;font-weight:bold;color:#f2efe6;text-shadow:0 1px 2px #000",
          String(d % 10)));
      }
      cell.appendChild(reel);
      frame.appendChild(cell);
      cells[place] = cell;
      reels[place] = reel;
    }
    this.root.appendChild(frame);
    return { frame, cells, reels };
  }

  /** Build the score counter (top-right) + its combo toast, and the cash counter (card mode). */
  private buildOdometer(): void {
    const s = this.buildOdoStrip(18, SCORE_DIGITS);
    this.scoreFrame = s.frame; this.scoreCells = s.cells; this.scoreReels = s.reels;

    // a STACK of descriptor lines (combos pile up during a climb instead of replacing)
    this.scoreToast = el("div",
      "position:absolute;top:60px;right:18px;display:flex;flex-direction:column;align-items:flex-end;gap:3px;pointer-events:none");
    this.root.appendChild(this.scoreToast);

    // the cash odometer sits just LEFT of the score strip; shown only in card mode
    const c = this.buildOdoStrip(210, CASH_DIGITS, "$");
    this.cashFrame = c.frame; this.cashCells = c.cells; this.cashReels = c.reels;

    this.renderScore(0);
  }

  /** Position each reel of a strip for `value` (a float mid-count). The ones wheel rolls
   *  continuously; a higher wheel only turns once every digit below it is a 9 — so the
   *  carry cascades together at the tick, like a real odometer, crisp at rest. */
  private renderStrip(cells: HTMLDivElement[], reels: HTMLDivElement[], value: number, digits: number): void {
    const v = Math.max(0, value);
    const sig = Math.max(1, String(Math.floor(v)).length); // significant digits, for dimming leading zeros
    const frac = v - Math.floor(v); // shared carry phase (the ones' sub-unit progress)
    for (let p = 0; p < digits; p++) {
      const step = Math.pow(10, p);
      const digit = Math.floor(v / step) % 10;
      const roll = p === 0 ? frac : (v % step >= step - 1 ? frac : 0); // carry only when the lower p digits are all 9
      reels[p].style.transform = `translateY(${-(digit + roll) * REEL_H}px)`;
      cells[p].style.opacity = p < sig ? "1" : "0.28";
    }
  }
  private renderScore(value: number): void { this.renderStrip(this.scoreCells, this.scoreReels, value, SCORE_DIGITS); }

  /** Show + set the card-mode cash odometer (snaps; cash both rises on kills and falls on spends). */
  showCash(cash: number): void { this.cashFrame.style.display = "flex"; this.renderStrip(this.cashCells, this.cashReels, Math.max(0, Math.floor(cash)), CASH_DIGITS); }
  hideCashOdo(): void { this.cashFrame.style.display = "none"; }

  /** Jump the counter to a value with no animation (run start / resume). */
  setScore(total: number): void {
    if (this.scoreRAF) { cancelAnimationFrame(this.scoreRAF); this.scoreRAF = 0; }
    this.scoreDisplayed = this.scoreTarget = Math.max(0, Math.floor(total));
    this.renderScore(this.scoreDisplayed);
    this.scoreToast.replaceChildren(); // drop any lingering combo lines
  }

  /** Set the new running total; the counter rolls up to it with ticking, and the
   *  combo descriptor (e.g. "DOUBLE BOUNCE LONG SHOT ×3") pops beneath it. */
  addScore(total: number, mult: number, desc: string): void {
    this.scoreTarget = Math.max(this.scoreTarget, Math.floor(total));
    if (desc) this.showScoreToast(desc, mult);
    if (!this.scoreRAF) {
      this.scoreBurstStart = this.scoreDisplayed;
      this.scoreRAF = requestAnimationFrame(this.stepScore);
    }
  }

  private stepScore = (): void => {
    const diff = this.scoreTarget - this.scoreDisplayed;
    if (diff <= 0.6) {
      this.scoreDisplayed = this.scoreTarget;
      this.renderScore(this.scoreDisplayed);
      this.scoreRAF = 0;
      return;
    }
    // very gentle ease-out — slow enough that rapid kills/shield-breaks visibly
    // STACK into one long climbing tally (the target keeps rising while this chases).
    this.scoreDisplayed = Math.min(this.scoreTarget, this.scoreDisplayed + Math.max(diff * 0.025, 1));
    this.renderScore(this.scoreDisplayed);
    const now = performance.now();
    if (now - this.scoreLastTick > 55) {
      this.scoreLastTick = now;
      const span = this.scoreTarget - this.scoreBurstStart;
      const prog = span > 0 ? (this.scoreDisplayed - this.scoreBurstStart) / span : 1;
      sfx.tick(prog);
    }
    this.scoreRAF = requestAnimationFrame(this.stepScore);
  };

  /** Pop a new descriptor line onto the stack — combos pile up beneath the
   *  odometer (each line pops in, lingers, then fades on its own timer). */
  private showScoreToast(desc: string, mult: number): void {
    const multTxt = mult > 1 ? ` <span style="color:#ffd86b">×${mult}</span>` : "";
    const line = el("div",
      "opacity:0;text-align:right;white-space:nowrap;font-size:15px;font-weight:bold;letter-spacing:1px;" +
      "text-shadow:0 2px 7px #000,0 0 14px rgba(0,0,0,0.6);transition:none",
      `<span style="color:#7fd0ff">${this.escape(desc)}</span>${multTxt}`);
    line.style.transform = "translateY(4px) scale(1.18)";
    this.scoreToast.appendChild(line);
    while (this.scoreToast.childElementCount > 6) this.scoreToast.firstElementChild!.remove(); // cap the stack
    void line.offsetWidth; // reflow so the settle transition runs from the popped state
    line.style.transition = "opacity 0.35s ease, transform 0.4s cubic-bezier(.2,.85,.3,1)";
    line.style.opacity = "1";
    line.style.transform = "translateY(0) scale(1)";
    setTimeout(() => {
      line.style.transition = "opacity 0.5s ease, transform 0.5s ease";
      line.style.opacity = "0";
      line.style.transform = "translateY(-10px) scale(0.96)";
    }, 1900);
    setTimeout(() => line.remove(), 2450);
  }

  flashBanner(text: string, ms = 1600): void {
    this.banner.textContent = text;
    this.banner.style.display = "block";
    this.banner.style.opacity = "1";
    if (this.bannerTimer) clearTimeout(this.bannerTimer);
    this.bannerTimer = setTimeout(() => { this.banner.style.display = "none"; }, ms);
  }

  // ---- card mode: kill feed -------------------------------------------

  /** Append a kill-feed line: "KILLER ▸ VICTIM · Nm" — BLUE ally / RED enemy, TYPE names, distance. */
  pushKill(killer: Tank | undefined, victim: Tank): void {
    const lab = (t?: Tank) => t ? { txt: t.type?.name ?? "You", col: t.team === 0 ? "#5ad1ff" : "#ff6a5a" } : { txt: "—", col: "#9aa6b5" };
    const k = lab(killer), v = lab(victim);
    const dist = killer ? Math.round(Math.hypot(killer.pos.x - victim.pos.x, killer.pos.z - victim.pos.z)) : 0;
    const line = el("div",
      "opacity:0;white-space:nowrap;font-size:14px;font-weight:bold;letter-spacing:0.4px;text-shadow:0 2px 6px #000,0 0 10px rgba(0,0,0,0.6);transition:none",
      `<span style="color:${k.col}">${this.escape(k.txt)}</span>` +
      `<span style="color:#c8d2de"> ▸ </span>` +
      `<span style="color:${v.col}">${this.escape(v.txt)}</span>` +
      (killer ? ` <span style="color:#8a96a5">· ${dist}m</span>` : ""));
    line.style.transform = "translateX(-8px)";
    this.killFeed.appendChild(line);
    while (this.killFeed.childElementCount > 6) this.killFeed.firstElementChild!.remove();
    void line.offsetWidth; // reflow so the pop-in transition runs
    line.style.transition = "opacity 0.25s ease, transform 0.3s cubic-bezier(.2,.85,.3,1)";
    line.style.opacity = "1"; line.style.transform = "translateX(0)";
    setTimeout(() => { line.style.transition = "opacity 0.5s ease, transform 0.5s ease"; line.style.opacity = "0"; line.style.transform = "translateX(-10px)"; }, 3000);
    setTimeout(() => line.remove(), 3550);
  }
  clearKillFeed(): void { this.killFeed.replaceChildren(); }

  // ---- card mode: round-start deploy phase ----------------------------

  /** Show the "START ROUND" button above the hand bar (deploy phase). */
  showStartRound(): void {
    if (!this.startBtn) {
      const b = document.createElement("button");
      b.style.cssText = "position:absolute;bottom:188px;left:50%;transform:translateX(-50%);pointer-events:auto;cursor:pointer;" +
        `font-family:${FONT};font-weight:900;font-size:20px;letter-spacing:1px;color:#0b1016;padding:10px 28px;border-radius:9px;border:1px solid #ffe49a;` +
        "background:linear-gradient(180deg,#ffe27a,#f5b833);box-shadow:0 6px 18px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.5)";
      b.textContent = "START ROUND  ⏎";
      b.addEventListener("mousedown", (e) => { e.stopPropagation(); e.preventDefault(); }); // don't bleed into fire/placement
      b.onclick = (e) => { e.stopPropagation(); this.onStartRound(); };
      this.startBtn = b;
      this.root.appendChild(b);
    }
    this.startBtn.style.display = "block";
  }
  hideStartRound(): void { if (this.startBtn) this.startBtn.style.display = "none"; }

  achievementToast(a: Achievement): void {
    const t = el("div",
      "padding:12px 16px;background:rgba(20,26,36,0.95);border:1px solid #ffd86b;" +
      "box-shadow:0 8px 24px rgba(0,0,0,0.5);min-width:240px;text-align:left;transition:opacity 0.4s",
      `<div style="font-size:12px;color:#ffd86b;letter-spacing:1px">ACHIEVEMENT UNLOCKED</div>
       <div style="font-weight:bold;margin-top:2px">${this.escape(a.name)}</div>
       <div style="font-size:12px;color:#9aa6b5">${this.escape(a.desc)}</div>`);
    this.toasts.appendChild(t);
    setTimeout(() => { t.style.opacity = "0"; }, 4200);
    setTimeout(() => t.remove(), 4700);
  }

  setVolumeValue(v: number): void {
    this.volume = v;
  }

  setMusicVolumeValue(v: number): void {
    this.musicVolume = v;
  }

  /** "Now playing" credit toast — clones the points-odometer glass (brushed rim + dark readout cell)
   *  and slides UP from the bottom-right, auto-hiding after ~5.5s. Shows the track + format · channels. */
  musicCreditToast(t: MusicTrack): void {
    const card = el("div",
      `display:flex;flex-direction:column;padding:5px 7px;border-radius:7px;background:${METAL_FRAME};` +
      `box-shadow:${FRAME_SHADOW};border:1px solid ${METAL_EDGE};min-width:210px;max-width:320px;` +
      "opacity:0;transform:translateY(16px);transition:opacity .35s ease,transform .4s cubic-bezier(.2,.85,.3,1)");
    // some tracks' descriptions list no format and/or no channel count — show only what we have
    const detail = [t.format ? this.escape(t.format) : "", t.channels ? `${t.channels} channels` : ""].filter(Boolean).join(" · ");
    const inner = el("div",
      "padding:6px 11px;border-radius:2px;text-align:right;border-left:1px solid #0a0b0d;border-right:1px solid #0a0b0d;" +
      "background:" + METAL_CELL + ";box-shadow:inset 0 0 5px rgba(0,0,0,0.85),inset 0 1px 0 rgba(255,255,255,0.05)",
      `<div style="font-size:10px;letter-spacing:2px;color:${GOLD};text-shadow:0 1px 2px #000">♪ NOW PLAYING</div>` +
      `<div style="font-weight:bold;font-size:15px;color:#f2efe6;text-shadow:0 1px 2px #000;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${this.escape(t.title)}</div>` +
      (detail ? `<div style="font-size:11px;color:#9aa6b5;letter-spacing:0.5px">${detail}</div>` : ""));
    card.appendChild(inner);
    this.musicToasts.appendChild(card);
    while (this.musicToasts.childElementCount > 2) this.musicToasts.firstElementChild!.remove(); // no pile-up
    void card.offsetWidth; // reflow so the transition runs
    card.style.opacity = "1"; card.style.transform = "translateY(0)";
    setTimeout(() => { card.style.opacity = "0"; card.style.transform = "translateY(16px)"; }, 5000);
    setTimeout(() => card.remove(), 5500);
  }

  private escape(s: string): string {
    return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
  }
}
